package net.frozenbit.games.soundgame.viewmodels

import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import net.frozenbit.games.soundgame.audio.AudioManager
import net.frozenbit.games.soundgame.audio.MusicManager
import net.frozenbit.games.soundgame.dtos.*
import net.frozenbit.games.soundgame.utils.TimeCorrection
import net.frozenbit.games.soundgame.utils.withPrevious
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

internal class AudioEventGenerator(
    private val timeCorrection: TimeCorrection,
) {
    private val phaseData = MutableStateFlow<PhaseData?>(null)

    sealed class VotingPlaybackState {
        /**
         * No playback. Will play [optionIndex] soon if set.
         */
        data class Idle(
            val optionIndex: Int? = null,
        ) : VotingPlaybackState()

        data object InitialPromptAnnouncement : VotingPlaybackState()
        data class VotingOptionsPresentation(
            val optionIndex: Int,
            val soundNumber: Int,
        ) : VotingPlaybackState()

        data class VotingOptionsPresentationFinished(
            val optionIndex: Int,
        ) : VotingPlaybackState()

        data object Finished : VotingPlaybackState()
        data object WinnerFinished : VotingPlaybackState()

        data class Replaying(
            val optionIndex: Int,
            val soundNumber: Int,
        ) : VotingPlaybackState()
    }

    private val _votingPlaybackState = MutableStateFlow<VotingPlaybackState>(VotingPlaybackState.Idle())

    /**
     * Playback information. Allows UI animation to be synchronized with the audio playback.
     */
    val votingPlaybackState: StateFlow<VotingPlaybackState> = _votingPlaybackState

    suspend fun play(audioManager: AudioManager, musicManager: MusicManager): Unit = coroutineScope {
        launch {
            var mainThemePosition = 0.0
            phaseData
                .selectMusic()
                .withPrevious()
                .collectLatest { (previousSong, newSong) ->
                    println("[music] ${previousSong?.fileName} → ${newSong?.fileName}")
                    if (previousSong is SongRequest.MainTheme) {
                        mainThemePosition = musicManager.getMusicPosition()
                    }
                    if (newSong is SongRequest.LobbyTheme) {
                        mainThemePosition = 0.0
                    }
                    if (newSong != null) {
                        musicManager.setMusicTrack(
                            fileName = newSong.fileName,
                            loop = newSong.loop,
                            position = when {
                                newSong is SongRequest.MainTheme && !newSong.restart ->
                                    mainThemePosition

                                else -> 0.0
                            },
                        )
                    } else {
                        musicManager.pauseMusic()
                    }
                }
        }
        launch {
            audioManager.soundEffectOn(
                { (it as? PhaseData.LobbyPhase)?.packet?.players?.size },
                file = "cue_player_joined.ogg",
            ) { oldValue, newValue ->
                oldValue != null && newValue != null && newValue > oldValue
            }
        }
        launch {
            audioManager.soundEffectOn(
                { it is PhaseData.PromptSelection },
                file = "cue_next_round.ogg",
            ) { wasInPromptSelection, isInPromptSelection ->
                wasInPromptSelection != null && isInPromptSelection != null
                        && isInPromptSelection && !wasInPromptSelection
            }
        }
        launch {
            audioManager.soundEffectOn(
                {
                    (it as? PhaseData.LobbyPhase)?.packet?.players?.map { player ->
                        player.avatarIndex
                    }
                },
                file = "cue_avatar_changed.ogg",
            ) { oldValue, newValue ->
                oldValue != null && newValue != null
                        && oldValue.size == newValue.size
                        && oldValue != newValue
            }
        }
        launch {
            countdownMusic(musicManager) { currentPhase ->
                when (currentPhase) {
                    is PhaseData.PromptSelection ->
                        currentPhase.packet.expiresAt

                    is PhaseData.SoundSelection ->
                        currentPhase.packet.expiresAt

                    else ->
                        null
                }?.let { timeCorrection.actual(it) }
            }
        }
        launch {
            initialPromptAnnoncement(audioManager)
        }
        launch {
            votingOptionsPresenter(audioManager)
        }
        launch {
            winnerReplay(audioManager)
        }
    }

    private suspend fun <T> AudioManager.soundEffectOn(
        data: (PhaseData?) -> T?,
        file: String,
        condition: (oldValue: T?, newValue: T?) -> Boolean,
    ) {
        phaseData
            .map { data(it) }
            .distinctUntilChanged()
            .withPrevious()
            .collectLatest { (oldValue, newValue) ->
                if (condition(oldValue, newValue)) {
                    playSoundEffect(file)
                }
            }
    }

    private suspend fun initialPromptAnnoncement(audioManager: AudioManager) {
        phaseData
            .map { (it as? PhaseData.SoundSelection)?.packet?.prompt?.id }
            .distinctUntilChanged()
            .collectLatest { currentPromptId ->
                if (currentPromptId != null) {
                    audioManager.enqueueSoundEffects(
                        listOf(
                            "cue_next_prompt_1.ogg",
                            "prompt_${currentPromptId}.ogg",
                        ),
                        duckMusic = true,
                    )
                }
            }
    }

    sealed class SongRequest {
        abstract val fileName: String
        abstract val loop: Boolean

        data class MainTheme(
            val restart: Boolean = false,
        ) : SongRequest() {
            override val fileName: String = "music_main_theme.ogg"
            override val loop: Boolean = true
        }

        data object LobbyTheme : SongRequest() {
            override val fileName: String = "music_lobby.ogg"
            override val loop: Boolean = true
        }

        data object Countdown30s : SongRequest() {
            override val fileName: String = "music_waiting_30s.ogg"
            override val loop: Boolean = false
        }

        data object GameFinished : SongRequest() {
            override val fileName: String = "music_game_finished.ogg"
            override val loop: Boolean = false
        }
    }

    private fun Flow<PhaseData?>.selectMusic() = transformLatest { phaseData ->
        when (phaseData) {
            is PhaseData.LobbyPhase -> {
                emit(SongRequest.LobbyTheme)
            }

            is PhaseData.PromptSelection -> {
                delay(500.milliseconds)
                emit(SongRequest.MainTheme())
            }
            is PhaseData.SoundSelection -> emit(SongRequest.MainTheme())
            is PhaseData.VotingPhase -> emit(null)
            is PhaseData.WinnerPhase -> emit(null)
            is PhaseData.GameOverPhase -> emit(SongRequest.GameFinished)
            null -> emit(null)
        }
    }.distinctUntilChanged()

    private suspend fun countdownMusic(
        musicManager: MusicManager,
        clock: Clock = Clock.System,
        musicFileName: String = "music_waiting_30s.ogg",
        musicDuration: Duration = 30.seconds,
        endsAt: (PhaseData?) -> Instant?,
    ) {
        phaseData
            .map { endsAt(it) }
            .distinctUntilChanged()
            .collectLatest { endTime ->
                if (endTime == null || endTime - clock.now() > 5.seconds) {
                    return@collectLatest
                }
                val delayDuration = (endTime - musicDuration) - clock.now()
                println("Countdown music will start in $delayDuration")
                if (delayDuration > Duration.ZERO) {
                    delay(delayDuration)
                }
                println("Countdown music starting now")

                println("Countdown music started")
            }
    }

    private suspend fun votingOptionsPresenter(
        audioManager: AudioManager,
    ) = coroutineScope {
        phaseData
            .map {
                (it as? PhaseData.VotingPhase)?.packet?.let { packet ->
                    packet.prompt to packet.options
                }
            }
            .distinctUntilChanged()
            .collectLatest { data ->
                if (data == null) {
                    _votingPlaybackState.value = VotingPlaybackState.Idle(null)
                    return@collectLatest
                }
                val (prompt, options) = data
                _votingPlaybackState.value = VotingPlaybackState.InitialPromptAnnouncement
                // Annonce prompt again
                audioManager.playSoundEffect("prompt_${prompt.id}.ogg")
                delay(2.seconds)
                for ((index, option) in options.withIndex()) {
                    _votingPlaybackState.value = VotingPlaybackState.Idle(optionIndex = index)
                    delay(1.seconds)
                    playEntry(index, audioManager, sound1Id = option.sound1Id, sound2Id = option.sound2Id)
                    _votingPlaybackState.value = VotingPlaybackState.Idle(optionIndex = index + 1)
                    delay(2.seconds)
                }
                _votingPlaybackState.value = VotingPlaybackState.Finished
            }
    }

    private suspend fun playEntry(
        index: Int,
        audioManager: AudioManager,
        isReplay: Boolean = false,
        sound1Id: String,
        sound2Id: String,
    ) {
        if (!isReplay) {
            _votingPlaybackState.value = VotingPlaybackState.VotingOptionsPresentation(
                optionIndex = index,
                soundNumber = 1,
            )
        } else {
            _votingPlaybackState.value = VotingPlaybackState.Replaying(
                optionIndex = index,
                soundNumber = 1,
            )
        }
        audioManager.playSoundEffect("sound_$sound1Id.ogg")
        _votingPlaybackState.value = if (!isReplay) {
            VotingPlaybackState.VotingOptionsPresentation(
                optionIndex = index,
                soundNumber = 2,
            )
        } else {
            VotingPlaybackState.Replaying(
                optionIndex = index,
                soundNumber = 2,
            )
        }
        audioManager.playSoundEffect("sound_$sound2Id.ogg")
        if (!isReplay) {
            _votingPlaybackState.value = VotingPlaybackState.VotingOptionsPresentationFinished(
                optionIndex = index,
            )
        }
        delay(2.seconds)
        if (isReplay) {
            _votingPlaybackState.value = VotingPlaybackState.Finished
        }
    }

    suspend fun replayEntry(audioManager: AudioManager, entryIndex: Int) {
        val currentPhase = phaseData.value
        if (currentPhase !is PhaseData.VotingPhase) {
            return
        }
        val option = currentPhase.packet.options.getOrNull(entryIndex)
            ?: return
        playEntry(entryIndex, audioManager, isReplay = true, option.sound1Id, option.sound2Id)
    }

    suspend fun winnerReplay(audioManager: AudioManager) {
        data class EntryAudio(
            val index: Int,
            val sound1Id: String,
            val sound2Id: String,
        )
        phaseData
            .map { phase ->
                (phase as? PhaseData.WinnerPhase)?.packet?.let { packet ->
                    val winnerIndex = packet.entries.indexOfFirst { it.winner }
                        .takeIf { it != -1 }
                    val winnerEntry = winnerIndex?.let { packet.entries[it] }
                    winnerEntry?.let { entry ->
                        packet.prompt to EntryAudio(
                            index = winnerIndex,
                            sound1Id = entry.sound1Id,
                            sound2Id = entry.sound2Id,
                        )
                    }
                }
            }
            .distinctUntilChanged()
            .collectLatest { data ->
                val (prompt, winner) = data
                    ?: return@collectLatest

                _votingPlaybackState.value = VotingPlaybackState.InitialPromptAnnouncement
                audioManager.playSoundEffect("prompt_${prompt.id}.ogg")
                delay(1.seconds)
                playEntry(winner.index, audioManager, sound1Id = winner.sound1Id, sound2Id = winner.sound2Id)
                _votingPlaybackState.value = VotingPlaybackState.WinnerFinished
                audioManager.playSoundEffect("cue_score.ogg", 0.8f)
            }
    }

    fun feed(packet: HostServerMessage) {
        when (packet) {
            is HostLobbyUpdate ->
                phaseData.value = PhaseData.LobbyPhase(packet)

            is HostPromptSelectionUpdate ->
                phaseData.value = PhaseData.PromptSelection(packet)

            is HostSoundSelectionUpdate ->
                phaseData.value = PhaseData.SoundSelection(packet)

            is HostVotingUpdate ->
                phaseData.value = PhaseData.VotingPhase(packet)

            is HostWinnerUpdate ->
                phaseData.value = PhaseData.WinnerPhase(packet)

            is HostGameOverUpdate ->
                phaseData.value = PhaseData.GameOverPhase(packet)

            else -> {}
        }
    }

    sealed class PhaseData {
        data class LobbyPhase(
            val packet: HostLobbyUpdate,
        ) : PhaseData()

        data class PromptSelection(
            val packet: HostPromptSelectionUpdate,
        ) : PhaseData()

        data class SoundSelection(
            val packet: HostSoundSelectionUpdate,
        ) : PhaseData()

        data class VotingPhase(
            val packet: HostVotingUpdate,
        ) : PhaseData()

        data class WinnerPhase(
            val packet: HostWinnerUpdate,
        ) : PhaseData()

        data class GameOverPhase(
            val packet: HostGameOverUpdate,
        ) : PhaseData()
    }
}